import { useState, useEffect } from 'react';
import type { Flashcard, StudySettings, StudyResult } from '../types';
import FlashcardFlip from './FlashcardFlip';
import { Button } from './ui/Button';
import {
  ThumbsUp,
  ThumbsDown,
  SkipForward,
  ChevronLeft,
  ChevronRight,
  Volume2,
  Star,
} from 'lucide-react';

interface StudyModeProps {
  cards: Flashcard[];
  settings?: Partial<StudySettings>;
  onComplete?: (results: StudyResult[]) => void;
  onExit?: () => void;
  className?: string;
}

export const StudyMode: React.FC<StudyModeProps> = ({
  cards,
  settings = {},
  onComplete,
  onExit,
  className = '',
}) => {
  const [studyCards, setStudyCards] = useState<Flashcard[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [results, setResults] = useState<StudyResult[]>([]);
  const [_sessionStartTime] = useState(Date.now());
  const [isFinished, setIsFinished] = useState(false);
  const [cardStartTime, setCardStartTime] = useState(Date.now());

  const defaultSettings: StudySettings = {
    shuffle: true,
    showHints: true,
    autoAdvance: false,
    trackProgress: true,
    studyNewCards: true,
    studyReviewCards: true,
    cardsPerSession: 20,
  };

  const studySettings = { ...defaultSettings, ...settings };

  useEffect(() => {
    let preparedCards = [...cards];
    if (studySettings.shuffle) {
      preparedCards = preparedCards.sort(() => Math.random() - 0.5);
    }
    preparedCards = preparedCards.slice(0, studySettings.cardsPerSession);
    setStudyCards(preparedCards);
    setCurrentIndex(0);
    setResults([]);
    setIsFinished(false);
    setShowAnswer(false);
    setCardStartTime(Date.now());
  }, [cards, studySettings.shuffle, studySettings.cardsPerSession]);

  const handleQualityRating = (quality: number) => {
    const timeSpent = Date.now() - cardStartTime;
    const card = studyCards[currentIndex];

    const result: StudyResult = {
      cardId: card.id,
      quality,
      timeSpent,
      timestamp: new Date().toISOString(),
    };

    const newResults = [...results, result];
    setResults(newResults);

    if (currentIndex + 1 >= studyCards.length) {
      setIsFinished(true);
      onComplete?.(newResults);
    } else {
      setCurrentIndex((prev) => prev + 1);
      setShowAnswer(false);
      setCardStartTime(Date.now());
    }
  };

  const handleSkip = () => {
    if (currentIndex + 1 >= studyCards.length) {
      setIsFinished(true);
      onComplete?.(results);
    } else {
      setCurrentIndex((prev) => prev + 1);
      setShowAnswer(false);
      setCardStartTime(Date.now());
    }
  };

  const handleSpeak = () => {
    const card = studyCards[currentIndex];
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(
        showAnswer ? card.back : card.front
      );
      window.speechSynthesis.speak(utterance);
    }
  };

  if (studyCards.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600 dark:text-gray-400">No cards available to study.</p>
        <Button onClick={onExit} className="mt-4">
          Go Back
        </Button>
      </div>
    );
  }

  if (isFinished) {
    const correct = results.filter((r) => r.quality >= 3).length;
    const total = results.length;
    const _accuracy = total > 0 ? Math.round((correct / total) * 100) : 0;

    return (
      <div className={`space-y-8 ${className}`}>
        <div className="text-center space-y-4">
          <div className="w-20 h-20 mx-auto bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
            <Star className="w-10 h-10 text-green-600 dark:text-green-400" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Session Complete!</h2>
          <p className="text-gray-600 dark:text-gray-400">You reviewed {total} cards</p>
        </div>
        <ProgressTracker results={results} totalCards={studyCards.length} />
        <div className="flex justify-center gap-3">
          <Button onClick={onExit} variant="primary">
            Back to Decks
          </Button>
        </div>
      </div>
    );
  }

  const currentCard = studyCards[currentIndex];
  const progress = studyCards.length > 0 ? ((currentIndex + 1) / studyCards.length) * 100 : 0;

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Progress bar */}
      <div className="space-y-2">
        <div className="flex justify-between text-sm text-gray-500 dark:text-gray-400">
          <span>
            Card {currentIndex + 1} of {studyCards.length}
          </span>
          <span>{Math.round(progress)}% complete</span>
        </div>
        <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
          <div
            className="h-full bg-blue-500 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Flashcard */}
      <div className="flex justify-center">
        <div className="w-full max-w-lg">
          <FlashcardFlip
            card={currentCard}
            onFlip={(flipped) => setShowAnswer(flipped)}
            showHint={studySettings.showHints}
          />
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-center gap-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleSpeak}
          title="Read aloud"
        >
          <Volume2 className="w-4 h-4" />
        </Button>
        <Button variant="ghost" size="sm" onClick={handleSkip} title="Skip card">
          <SkipForward className="w-4 h-4" />
        </Button>

        <div className="w-px h-6 bg-gray-200 dark:bg-gray-700" />

        {/* Difficulty rating */}
        <div className="flex items-center gap-2">
          <Button
            variant="danger"
            size="sm"
            onClick={() => handleQualityRating(0)}
            title="Forgot"
          >
            <ThumbsDown className="w-4 h-4" />
            Forgot
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => handleQualityRating(3)}
            title="Hard"
          >
            Hard
          </Button>
          <Button
            variant="success"
            size="sm"
            onClick={() => handleQualityRating(5)}
            title="Easy"
          >
            <ThumbsUp className="w-4 h-4" />
            Easy
          </Button>
        </div>

        <div className="w-px h-6 bg-gray-200 dark:bg-gray-700" />

        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setShowAnswer(false);
            setCurrentIndex(Math.max(0, currentIndex - 1));
          }}
          disabled={currentIndex === 0}
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setShowAnswer(false);
            setCurrentIndex(Math.min(studyCards.length - 1, currentIndex + 1));
          }}
          disabled={currentIndex === studyCards.length - 1}
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>

      {/* Exit */}
      <div className="flex justify-center">
        <Button variant="ghost" onClick={onExit} className="text-sm text-gray-500">
          Exit Session
        </Button>
      </div>
    </div>
  );
};

export default StudyMode;
